import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  HostBinding,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { filter, Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../firebase/auth.service';
import {
  AlertController,
  PopoverController,
  ToastController,
  IonContent,
  NavController,
} from '@ionic/angular';
import { CommentActionsMenuComponent } from '../../../activity-feed/comment-actions-menu/comment-actions-menu.component';
import { ReactionsService } from '../../../services/reactions.service';
import { Router } from '@angular/router';
import { UserService } from '../../../auth/user.service';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { environment } from '../../../../environments/environment';

// Comment interface
interface Comment {
  id: string;
  userId: string;
  username: string;
  userProfilePic: string;
  text: string;
  timestamp: any;
  likes?: number;
  likedBy?: string[];
  parentId?: string;
  parentUsername?: string;
  replies?: Comment[];
  isReplying?: boolean;
  isDeleted?: boolean;
  isEdited?: boolean;
  reactions?: { type: string; userId: string }[];
  showOptionsMenu?: boolean;
  showReplies?: boolean;
}

@Component({
  selector: 'app-comments-section',
  templateUrl: './comments-section.component.html',
  styleUrls: ['./comments-section.component.scss'],
})
export class CommentsSectionComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() itemId: string;
  @Input() itemType: string;
  @Input() footerDocked = false;
  @ViewChild('commentInput') commentInput: ElementRef;

  @HostBinding('class.footer-docked-comments')
  get isFooterDocked() {
    return this.footerDocked;
  }

  currentUser: any;
  comments: Comment[] = [];
  filteredComments: Comment[] = [];
  newComment: string = '';
  replyText: string = '';
  replyingToComment: string | null = null;
  isLoadingComments: boolean = true;
  commentsLocked: boolean = false;

  private destroy$ = new Subject<void>();

  static retryCount: number = 0;

  // Add this property to track the last check time
  private lastTokenCheckTime: number = 0;
  private tokenCheckCooldown: number = 60000; // 1 minute cooldown

  constructor(
    private fireStore: AngularFirestore,
    private authService: AuthService,
    private popoverCtrl: PopoverController,
    private reactionsService: ReactionsService,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private router: Router,
    private userService: UserService,
    private navCtrl: NavController
  ) {}

  ngOnInit() {
    this.getUser();
    this.userService.currentUser$
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user?.uid)
      )
      .subscribe(user => {
        if (!this.currentUser || this.currentUser.uid === user.uid) {
          this.currentUser = user;
          this.checkIfCommentsUnlocked();
        }
      });
    this.checkIfCommentsUnlocked();
  }

  ngAfterViewInit() {
    // Check if the URL has a query param to focus the comment input
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('focusComments') === 'true') {
      setTimeout(() => {
        this.focusCommentInput();
      }, 500);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUser() {
    const currentAuthUser = this.authService.getCurrentUser();
    if (currentAuthUser) {
      this.authService.getUserByUid(currentAuthUser.uid).subscribe(user => {
        this.currentUser = user;
      });
    }
  }

  loadComments() {
    if (this.commentsLocked) {
      this.isLoadingComments = false;
      return;
    }

    if (!this.itemId) {
      this.isLoadingComments = false;
      return;
    }

    this.isLoadingComments = true;
    const loadingFallback = setTimeout(() => {
      this.isLoadingComments = false;
    }, 8000);

    // Create field name based on item type
    let fieldName = 'postId';
    if (this.itemType === 'events') {
      fieldName = 'eventId';
    } else if (
      this.itemType === 'event_images' ||
      this.itemType === 'post_images'
    ) {
      fieldName = 'imageId';
    }

    this.fireStore
      .collection('comments', ref =>
        ref.where(fieldName, '==', this.itemId).orderBy('timestamp', 'asc')
      )
      .snapshotChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        actions => {
          const allComments = actions.map(action => {
            const data = action.payload.doc.data() as any;
            return {
              id: action.payload.doc.id,
              ...data,
              likes: data.likes || 0,
              likedBy: data.likedBy || [],
              replies: [] as Comment[],
              isReplying: false,
            };
          });

          // First, find all top-level comments (no parentId)
          const parentComments = allComments.filter(
            comment => !comment.parentId
          );

          // Then, organize replies into a hierarchy
          const replies = allComments.filter(comment => comment.parentId);

          // Function to recursively attach replies
          const attachReplies = (comments: Comment[]) => {
            comments.forEach(comment => {
              // Find direct replies to this comment
              const directReplies = replies.filter(
                reply => reply.parentId === comment.id
              );
              comment.replies = directReplies;

              // Recursively attach nested replies
              if (comment.replies.length > 0) {
                attachReplies(comment.replies);
              }
            });
          };

          // Attach all replies to their parent comments
          attachReplies(parentComments);

          this.comments = parentComments;
          this.sortComments();
          clearTimeout(loadingFallback);
          this.isLoadingComments = false;
        },
        error => {
          console.error('Error loading comments:', error);
          clearTimeout(loadingFallback);
          this.isLoadingComments = false;
        }
      );
  }

  sortComments() {
    // Always sort comments chronologically (oldest first, newest at bottom)
    this.filteredComments = [...this.comments].sort((a, b) => {
      const timeA = a.timestamp?.toDate
        ? a.timestamp.toDate().getTime()
        : a.timestamp instanceof Date
        ? a.timestamp.getTime()
        : 0;
      const timeB = b.timestamp?.toDate
        ? b.timestamp.toDate().getTime()
        : b.timestamp instanceof Date
        ? b.timestamp.getTime()
        : 0;

      return timeA - timeB; // Ascending order (oldest first)
    });

    // Also ensure replies for each comment are sorted chronologically
    this.filteredComments.forEach(comment => {
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.sort((a, b) => {
          const timeA = a.timestamp?.toDate
            ? a.timestamp.toDate().getTime()
            : a.timestamp instanceof Date
            ? a.timestamp.getTime()
            : 0;
          const timeB = b.timestamp?.toDate
            ? b.timestamp.toDate().getTime()
            : b.timestamp instanceof Date
            ? b.timestamp.getTime()
            : 0;

          return timeA - timeB; // Ascending order (oldest first)
        });
      }
    });
  }

  addComment() {
    if (!this.currentUser) {
      return;
    }

    // Store current scroll position
    const commentsContainer = document.querySelector('.comments-list');
    const currentScrollPos = commentsContainer?.scrollTop || 0;

    // Use replyText when replying to a comment, otherwise use newComment
    const commentText = this.replyingToComment
      ? this.replyText
      : this.newComment;

    if (!commentText?.trim()) {
      return;
    }

    // Show loading state
    this.isLoadingComments = true;

    // Find parent comment/reply to get username and check nesting level
    let parentUsername = '';
    let nestingLevel = 0;

    if (this.replyingToComment) {
      const findParentCommentAndLevel = (
        comments: Comment[],
        level = 0
      ): { comment: Comment | null; level: number } => {
        for (const comment of comments) {
          if (comment.id === this.replyingToComment) {
            return { comment, level };
          }
          if (comment.replies) {
            const result = findParentCommentAndLevel(
              comment.replies,
              level + 1
            );
            if (result.comment) return result;
          }
        }
        return { comment: null, level: 0 };
      };

      const result = findParentCommentAndLevel(this.comments);
      const parentComment = result.comment;
      nestingLevel = result.level;

      // Check if we're trying to reply to a reply of a reply (i.e., third level)
      if (nestingLevel >= 2) {
        // Don't allow more than 2 levels of nesting
        this.toastCtrl
          .create({
            message:
              'To keep conversations readable, replies are limited to 2 levels deep.',
            duration: 3000,
            position: 'bottom',
            color: 'medium',
          })
          .then(toast => toast.present());

        this.isLoadingComments = false;
        return;
      }

      if (parentComment) {
        parentUsername = parentComment.username;
      }
    }

    // Create comment data
    const commentData: any = {
      userId: this.currentUser.uid,
      username: this.currentUser.username || 'Anonymous',
      userProfilePic: this.currentUser.profilePicture || '',
      text: commentText.trim(),
      timestamp: new Date(),
      likes: 0,
      likedBy: [],
      parentId: this.replyingToComment,
      parentUsername: parentUsername,
      type: 'comment',
      isDeleted: false,
    };

    // Set the appropriate ID field based on item type
    if (this.itemType === 'events') {
      commentData.eventId = this.itemId;
    } else if (
      this.itemType === 'event_images' ||
      this.itemType === 'post_images'
    ) {
      commentData.imageId = this.itemId;
    } else {
      commentData.postId = this.itemId;
    }

    this.fireStore
      .collection('comments')
      .add(commentData)
      .then(() => {
        // Clear input and reset state
        if (this.replyingToComment) {
          this.replyText = '';
        } else {
          this.newComment = '';
        }

        // Reset all reply states
        const resetReplyState = (comments: Comment[]) => {
          comments.forEach(c => {
            c.isReplying = false;
            if (c.replies) {
              resetReplyState(c.replies);
            }
          });
        };

        resetReplyState(this.comments);
        this.replyingToComment = null;

        // Restore scroll position after a short delay
        setTimeout(() => {
          if (commentsContainer) {
            if (!this.replyingToComment) {
              // For new comments, scroll to bottom
              commentsContainer.scrollTop = commentsContainer.scrollHeight;
            } else {
              // For replies, maintain position
              commentsContainer.scrollTop = currentScrollPos;
            }
          }
          this.isLoadingComments = false;
        }, 100);
      })
      .catch(error => {
        console.error('Error adding comment:', error);
        this.isLoadingComments = false;
        // Restore scroll position on error
        if (commentsContainer) {
          commentsContainer.scrollTop = currentScrollPos;
        }
      });
  }

  toggleReply(comment: Comment) {
    if (comment.isDeleted) {
      return;
    }

    // Check comment nesting level before allowing reply
    const checkNestingLevel = (
      comments: Comment[],
      targetId: string,
      level = 0
    ): number => {
      for (const c of comments) {
        if (c.id === targetId) {
          return level;
        }
        if (c.replies?.length > 0) {
          const nestedLevel = checkNestingLevel(c.replies, targetId, level + 1);
          if (nestedLevel > -1) return nestedLevel;
        }
      }
      return -1;
    };

    const nestingLevel = checkNestingLevel(this.comments, comment.id);

    // Don't allow replying to comments at level 2 (which would create level 3 comments)
    if (nestingLevel >= 2) {
      return;
    }

    // Store current scroll position
    const commentsContainer = document.querySelector('.comments-list');
    const currentScrollPos = commentsContainer?.scrollTop || 0;

    // If we're opening a reply form, close any other open reply forms
    if (!comment.isReplying) {
      const resetReplyState = (comments: Comment[]) => {
        comments.forEach(c => {
          c.isReplying = false;
          if (c.replies) {
            resetReplyState(c.replies);
          }
        });
      };

      resetReplyState(this.comments);
    }

    // Toggle reply state
    comment.isReplying = !comment.isReplying;
    this.replyingToComment = comment.isReplying ? comment.id : null;
    this.replyText = '';

    // Focus on input field after a short delay
    if (comment.isReplying) {
      setTimeout(() => {
        const replyInput = document.getElementById(`reply-input-${comment.id}`);
        if (replyInput) {
          replyInput.focus();
        }
        // Restore scroll position
        if (commentsContainer) {
          commentsContainer.scrollTop = currentScrollPos;
        }
      }, 100);
    }
  }

  likeComment(comment: Comment) {
    if (!this.currentUser) {
      return;
    }

    const userId = this.currentUser.uid;
    const hasLiked = comment.likedBy?.includes(userId);

    // If already liked, remove like
    if (hasLiked) {
      comment.likes = Math.max(0, (comment.likes || 0) - 1);
      comment.likedBy = comment.likedBy.filter(id => id !== userId);
    } else {
      // Otherwise, add like
      comment.likes = (comment.likes || 0) + 1;
      if (!comment.likedBy) {
        comment.likedBy = [];
      }
      comment.likedBy.push(userId);
    }

    // Update the comment in Firestore
    this.fireStore
      .collection('comments')
      .doc(comment.id)
      .update({
        likes: comment.likes,
        likedBy: comment.likedBy,
      })
      .catch(error => console.error('Error updating comment like:', error));
  }

  hasLikedComment(comment: Comment): boolean {
    return this.currentUser && comment.likedBy?.includes(this.currentUser.uid);
  }

  async showCommentActions(event: any, comment: Comment) {
    event.preventDefault();
    event.stopPropagation();

    // Check if comment is at max nesting level
    const isMaxLevel = this.isMaxNestingLevel(comment.id);

    const popover = await this.popoverCtrl.create({
      component: CommentActionsMenuComponent,
      event: event,
      componentProps: {
        comment: comment,
        isCurrentUserComment:
          this.currentUser && this.currentUser.uid === comment.userId,
        isMaxNestingLevel: isMaxLevel,
      },
      cssClass: 'comment-actions-popover',
      showBackdrop: false,
      backdropDismiss: true,
      keyboardClose: true,
    });

    await popover.present();

    const { data } = await popover.onWillDismiss();

    if (data) {
      switch (data.action) {
        case 'react':
          this.reactToComment(comment, data.reactionType);
          break;
        case 'reply':
          // Only allow reply action if not at max nesting level
          if (!isMaxLevel) {
            this.toggleReply(comment);
          }
          break;
        case 'edit':
          this.editComment(comment);
          break;
        case 'delete':
          this.deleteComment(comment);
          break;
        case 'flag':
          this.flagComment(comment);
          break;
        default:
          break;
      }
    }
  }

  reactToComment(comment: Comment, reactionType: string) {
    if (!this.currentUser) {
      return;
    }

    // Add reaction to the comment
    if (!comment.reactions) {
      comment.reactions = [];
    }

    // Check if user already has this reaction
    const existingReactionIndex = comment.reactions.findIndex(
      r => r.userId === this.currentUser.uid && r.type === reactionType
    );

    if (existingReactionIndex >= 0) {
      // Remove the reaction if it already exists
      comment.reactions.splice(existingReactionIndex, 1);
    } else {
      // Otherwise add the new reaction
      comment.reactions.push({
        type: reactionType,
        userId: this.currentUser.uid,
      });
    }

    // Update the comment in Firestore
    this.fireStore
      .collection('comments')
      .doc(comment.id)
      .update({
        reactions: comment.reactions,
      })
      .catch(error =>
        console.error('Error updating comment reactions:', error)
      );
  }

  deleteComment(comment: Comment) {
    if (!this.currentUser || this.currentUser.uid !== comment.userId) {
      return;
    }

    // Mark as deleted rather than actually deleting, to preserve thread structure
    this.fireStore
      .collection('comments')
      .doc(comment.id)
      .update({
        text: 'This comment has been deleted',
        isDeleted: true,
      })
      .catch(error => console.error('Error deleting comment:', error));
  }

  focusCommentInput() {
    if (this.commentInput && this.commentInput.nativeElement) {
      this.commentInput.nativeElement.setFocus();
    }
  }

  formatDate(timestamp: any): string {
    if (!timestamp) {
      return '';
    }

    const date = timestamp.toDate ? timestamp.toDate() : timestamp;

    // Get time difference in seconds
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

    if (seconds < 60) {
      return 'just now';
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m ago`;
    } else if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(seconds / 86400);
      if (days < 7) {
        return `${days}d ago`;
      } else {
        return date.toLocaleDateString();
      }
    }
  }

  // Edit comment functionality
  editComment(comment: Comment) {
    if (!this.currentUser || this.currentUser.uid !== comment.userId) {
      return;
    }

    // Open a prompt to edit the comment
    this.alertCtrl
      .create({
        header: 'Edit Comment',
        inputs: [
          {
            name: 'text',
            type: 'text',
            value: comment.text,
            placeholder: 'Edit your comment...',
          },
        ],
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Save',
            handler: data => {
              if (!data.text || !data.text.trim()) {
                return false;
              }

              // Update the comment in Firestore
              this.fireStore
                .collection('comments')
                .doc(comment.id)
                .update({
                  text: data.text.trim(),
                  isEdited: true,
                })
                .then(() => {
                  // Update the comment locally
                  comment.text = data.text.trim();
                  comment.isEdited = true;
                })
                .catch(error =>
                  console.error('Error updating comment:', error)
                );
            },
          },
        ],
      })
      .then(alert => alert.present());
  }

  // Flag comment functionality
  flagComment(comment: Comment) {
    if (!this.currentUser) {
      // Redirect to login or show login dialog
      this.presentLoginRequiredAlert();
      return;
    }

    // Show flag modal with options
    this.alertCtrl
      .create({
        header: 'Flag This Comment',
        message: 'Please select a reason for flagging this comment:',
        inputs: [
          {
            name: 'inappropriate',
            type: 'radio',
            label: 'Inappropriate content',
            value: 'Inappropriate content',
            checked: true,
          },
          {
            name: 'spam',
            type: 'radio',
            label: 'Spam',
            value: 'Spam',
          },
          {
            name: 'harassment',
            type: 'radio',
            label: 'Harassment',
            value: 'Harassment',
          },
          {
            name: 'misinformation',
            type: 'radio',
            label: 'Misinformation',
            value: 'Misinformation',
          },
          {
            name: 'other',
            type: 'radio',
            label: 'Other issue',
            value: 'Other issue',
          },
        ],
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Flag',
            handler: reason => {
              if (reason === 'Other issue') {
                this.showCustomFlagReason(comment);
                return;
              }

              if (reason) {
                this.submitFlagComment(comment, reason);
              }
            },
          },
        ],
      })
      .then(alert => alert.present());
  }

  // Helper method to present login required alert
  private presentLoginRequiredAlert() {
    this.alertCtrl
      .create({
        header: 'Login Required',
        message: 'Please login to flag this comment.',
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Login',
            handler: () => {
              // Navigate to login page
              this.navCtrl.navigateForward('/login');
            },
          },
        ],
      })
      .then(alert => alert.present());
  }

  // Show custom flag reason input
  private showCustomFlagReason(comment: Comment) {
    this.alertCtrl
      .create({
        header: 'Other Issue',
        message: 'Please describe the issue:',
        inputs: [
          {
            name: 'customReason',
            type: 'text',
            placeholder: 'Describe the issue...',
          },
        ],
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Submit',
            handler: data => {
              if (data.customReason && data.customReason.trim() !== '') {
                this.submitFlagComment(comment, `Other: ${data.customReason}`);
              } else {
                return false; // Keep the dialog open if no reason provided
              }
            },
          },
        ],
      })
      .then(alert => alert.present());
  }

  // Submit flag to database
  private submitFlagComment(comment: Comment, reason: string) {
    // Create flag data
    const flagData = {
      type: 'comment',
      commentId: comment.id,
      postId: this.itemId,
      itemType: this.itemType || 'post', // Add itemType information
      commentText: comment.text,
      flaggedBy: this.currentUser.uid,
      flaggedByUsername: this.currentUser.username || 'Anonymous',
      flagReason: reason,
      timestamp: new Date(),
      status: 'pending',
    };

    // Generate a unique ID for the flag
    const flagId = this.fireStore.createId();

    // Add flag to the database
    this.fireStore
      .collection('flaggedContent')
      .doc(flagId)
      .set(flagData)
      .then(() => {
        // Notify admin about flagged comment
        this.notifyAdminAboutFlaggedComment(comment, reason);

        // Show confirmation toast
        this.toastCtrl
          .create({
            message: 'Comment flagged. Our moderators will review it.',
            duration: 2000,
            position: 'bottom',
          })
          .then(toast => toast.present());
      })
      .catch(error => {
        console.error('Error flagging comment:', error);
        this.toastCtrl
          .create({
            message: 'Error flagging comment. Please try again.',
            duration: 2000,
            position: 'bottom',
            color: 'danger',
          })
          .then(toast => toast.present());
      });
  }

  // Send email notification to support about flagged comment
  private notifyAdminAboutFlaggedComment(comment: Comment, reason: string) {
    // Get Firebase functions
    const functions = getFunctions();
    const sendAdminNotification = httpsCallable(functions, 'sendMail');

    // Prepare notification data
    const notificationData = {
      recipientEmail: 'admin@mermapp.com',
      subject: 'Comment Flagged: Action Required',
      message: `
        A comment has been flagged by a user:

        Comment Text: "${comment.text}"
        Comment ID: ${comment.id}
        Post ID: ${this.itemId}
        Content Type: ${this.itemType || 'post'}
        Flagged By: ${this.currentUser.username || 'Anonymous'} (${
        this.currentUser.uid
      })
        Flagged Reason: ${reason}

        Please review this comment at your earliest convenience.
      `,
      sender: 'Mermapp System',
    };

    // Send the notification
    sendAdminNotification(notificationData).catch(error => {
      console.error('Error sending admin notification:', error);
    });
  }

  // Check if comments are already unlocked for this post in localStorage
  async checkIfCommentsUnlocked() {
    if (!this.itemId || !this.currentUser) {
      // Only retry a few times, don't keep checking indefinitely
      // Use a static variable to track retries across instances
      CommentsSectionComponent.retryCount =
        (CommentsSectionComponent.retryCount || 0) + 1;

      // Limit to max 3 retries to prevent infinite loop
      if (CommentsSectionComponent.retryCount <= 3) {
        setTimeout(() => this.checkIfCommentsUnlocked(), 500);
      } else {
        // After max retries, just set as locked and stop checking
        this.commentsLocked = true;
        this.logCommentsAccess('missing-context', 'locked');
        CommentsSectionComponent.retryCount = 0;
      }
      return;
    }

    // Reset retry counter when we have the info
    CommentsSectionComponent.retryCount = 0;

    // First check if user has premium status, which includes all comments
    if (this.userService.isPremium || this.userService.isPaidPremium) {
      this.commentsLocked = false;
      this.logCommentsAccess('full-premium', 'unlocked');
      this.loadComments();
      return;
    }

    // Check if this specific post was already unlocked regardless of subscription status
    // Create a unique key for this user and post
    const unlockKey = `comments_unlocked_${this.currentUser.uid}_${this.itemId}`;
    const isUnlocked = localStorage.getItem(unlockKey) === 'true';

    // If the post was specifically unlocked, show comments and stop further checks
    if (isUnlocked) {
      this.commentsLocked = false;
      this.logCommentsAccess('post-unlock', 'unlocked');
      this.loadComments();
      return;
    }

    // If not specifically unlocked, check if the user has chat verification
    const isVerified = await this.checkChatVerification();
    if (isVerified) {
      this.commentsLocked = false;
      this.logCommentsAccess('chat-membership', 'unlocked');
      this.loadComments();
      return;
    }

    // If we reach here, comments are locked
    this.commentsLocked = true;
    this.logCommentsAccess('no-access', 'locked');
  }

  // Update the checkChatVerification method
  async checkChatVerification(): Promise<boolean> {
    // First check if already verified in memory
    if (this.authService.isChatVerified) {
      this.logCommentsAccess('chat-cache', 'unlocked');
      return true;
    }

    // Check if user has premium or chat membership from the UserService
    if (this.userService.isPaidPremium || this.userService.isPremium) {
      this.authService.isChatVerified = true;
      this.logCommentsAccess('full-premium-check', 'unlocked');
      return true;
    }

    // Check for this specific post being unlocked (via payment)
    const unlockKey = `comments_unlocked_${this.currentUser.uid}_${this.itemId}`;
    const isUnlocked = localStorage.getItem(unlockKey) === 'true';
    if (isUnlocked) {
      this.logCommentsAccess('post-unlock-check', 'unlocked');
      return true;
    }

    // Get current user from Firebase Auth
    const currentAuthUser = this.authService.getCurrentUser();
    if (!currentAuthUser) {
      this.logCommentsAccess('no-auth-user', 'locked');
      return false;
    }

    // Check Firestore user document
    const userRef = this.fireStore.collection('users').doc(currentAuthUser.uid);
    const userDoc = await userRef.get().toPromise();
    if (userDoc.exists && (userDoc.data() as any).isPersonalPremium) {
      this.authService.isChatVerified = true;
      this.logCommentsAccess('personal-premium-doc', 'unlocked');
      return true;
    }

    // Check for active subscriptions in Firestore
    try {
      const snapshot = await this.fireStore
        .collection('users')
        .doc(currentAuthUser.uid)
        .collection('subscriptions', ref =>
          ref.where('status', 'in', ['trialing', 'active'])
        )
        .get()
        .toPromise();

      if (snapshot && snapshot.docs && snapshot.docs.length > 0) {
        this.authService.isChatVerified = true;
        this.logCommentsAccess('active-subscription', 'unlocked');
        return true;
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }

    // Apply cooldown to token checks
    const now = Date.now();
    if (now - this.lastTokenCheckTime < this.tokenCheckCooldown) {
      // If we've checked recently, use cached value
      this.logCommentsAccess('token-cooldown', this.authService.isChatVerified ? 'unlocked' : 'locked');
      return this.authService.isChatVerified;
    }

    // Update last check time
    this.lastTokenCheckTime = now;

    // Check Firebase Auth token claims - without forcing refresh
    const decodedToken = await currentAuthUser.getIdTokenResult(false);
    this.authService.isChatVerified =
      decodedToken.claims.stripeRole === 'premium' ||
      decodedToken.claims.stripeMembership === 'premium';
    this.logCommentsAccess(
      'token-claims',
      this.authService.isChatVerified ? 'unlocked' : 'locked'
    );
    return this.authService.isChatVerified;
  }

  private logCommentsAccess(source: string, decision: 'locked' | 'unlocked') {
    if (environment.production) {
      return;
    }

    console.info('[comments-access]', {
      source,
      decision,
      uid: this.currentUser?.uid,
      itemId: this.itemId,
      itemType: this.itemType,
      isPremium: !!this.userService.isPremium,
      isPaidPremium: !!this.userService.isPaidPremium,
      isChatVerified: !!this.authService.isChatVerified,
    });
  }

  // Method to unlock comments via payment
  async unlockComments() {
    // Check if we're already on a comments page
    const isAlreadyOnCommentsPage =
      this.router.url.includes('events') ||
      this.router.url.includes('post') ||
      this.router.url.includes('item') ||
      this.router.url.includes('media');

    // Format item ID with prefix based on itemType
    let formattedItemId = this.itemId;
    if (this.itemType) {
      if (this.itemType === 'events') {
        formattedItemId = `event_${this.itemId}`;
      } else if (this.itemType === 'pod') {
        formattedItemId = `pod_${this.itemId}`;
      } else if (this.itemType === 'post') {
        formattedItemId = `post_${this.itemId}`;
      } else if (this.itemType === 'pools') {
        formattedItemId = `pool_${this.itemId}`;
      } else if (this.itemType === 'media') {
        formattedItemId = `media_${this.itemId}`;
      }
    }

    // Check if we're in a modal (like photo modal) by looking for active modals
    const activeModal = document.querySelector(
      'ion-modal.image-viewer-modal.show-modal'
    );
    const isInModal = activeModal !== null;

    // Store navigation parameters for later use
    const navParams = {
      returnTo: 'comments',
      itemId: formattedItemId,
      from: isAlreadyOnCommentsPage ? 'content' : null,
      // Add a timestamp to force fresh state
      ts: Date.now(),
    };

    // Store navigation intent in localStorage to ensure it persists through modal closing
    localStorage.setItem(
      'pendingCommentVerification',
      JSON.stringify(navParams)
    );

    if (isInModal) {
      // Find the close button in the modal
      const closeButton =
        activeModal.querySelector('ion-buttons[slot="end"] ion-button') ||
        activeModal.querySelector('ion-button[ng-reflect-name="close"]');

      if (closeButton) {
        // Click the close button to dismiss the modal
        (closeButton as HTMLElement).click();

        // Give the modal time to close, then navigate
        setTimeout(() => {
          // Clear any existing navigation history to ensure fresh state
          window.location.href = `/chat/unverified?returnTo=comments&itemId=${formattedItemId}${
            isAlreadyOnCommentsPage ? '&from=content' : ''
          }&ts=${Date.now()}`;
        }, 500);
        return;
      }
    }

    // If no modal or couldn't find close button, use direct navigation
    // Use window.location.href instead of router to ensure page refresh
    window.location.href = `/chat/unverified?returnTo=comments&itemId=${formattedItemId}${
      isAlreadyOnCommentsPage ? '&from=content' : ''
    }&ts=${Date.now()}`;
  }

  // Save unlocked state to localStorage
  saveUnlockedState() {
    const unlocked = JSON.parse(
      localStorage.getItem('unlockedComments') || '{}'
    );
    unlocked[this.itemId] = true;
    localStorage.setItem('unlockedComments', JSON.stringify(unlocked));
  }

  /**
   * Navigate to a user's profile page
   * @param userId The ID of the user whose profile to navigate to
   */
  navigateToUserProfile(userId: string) {
    if (userId) {
      this.router.navigate(['/profile', userId]);
    }
  }

  toggleReplies(comment: Comment) {
    comment.showReplies = !comment.showReplies;
  }

  // Check if a comment is at the maximum allowed nesting level
  isMaxNestingLevel(commentId: string): boolean {
    const checkNestingLevel = (
      comments: Comment[],
      targetId: string,
      level = 0
    ): number => {
      for (const c of comments) {
        if (c.id === targetId) {
          return level;
        }
        if (c.replies?.length > 0) {
          const nestedLevel = checkNestingLevel(c.replies, targetId, level + 1);
          if (nestedLevel > -1) return nestedLevel;
        }
      }
      return -1;
    };

    const nestingLevel = checkNestingLevel(this.comments, commentId);
    return nestingLevel >= 2;
  }
}

